import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { getAuthToken, removeAuthToken } from '../utils/cookieUtils';

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lastTokenRef = useRef(getAuthToken());
  const isHandlingConflictRef = useRef(false);

  const handleSessionConflict = useCallback((reason = 'session_expired') => {
    if (isHandlingConflictRef.current) return; // Don't run multiple times
    
    // Check if axios interceptor already handled this
    const axiosAlreadyHandled = sessionStorage.getItem('axios_handled_session_conflict') === 'true';
    if (axiosAlreadyHandled) {
      return; // Axios already showed the toast and is handling redirect
    }
    
    isHandlingConflictRef.current = true;

    // Clear everything
    removeAuthToken();
    localStorage.clear();
    sessionStorage.clear();
    setIsAuthenticated(false);

    // Show the right message
    const isConflict = reason.includes('conflict') || reason.includes('another');
    const message = isConflict 
      ? "Your session has expired because someone else logged in with your account."
      : "Your session has expired. Please log in again.";

    toast.error(message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'session-conflict', // Prevent duplicates
    });

    // Go back to login
    setTimeout(() => {
      window.location.href = "/login";
    }, 5000);
  }, []);

  const checkForSessionConflict = useCallback(async () => {
    const currentToken = getAuthToken();
    
    if (lastTokenRef.current && (!currentToken || currentToken !== lastTokenRef.current)) {
      const isNaturalLogout = sessionStorage.getItem('natural_logout') === 'true';
      
      if (!isNaturalLogout) {
        handleSessionConflict('session_conflict');
        return;
      }
    }

    lastTokenRef.current = currentToken;

    if (currentToken) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/validate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 401 || response.status === 403) {
          handleSessionConflict('token_invalid');
        }
      } catch (error) {
        // Network errors don't count as session conflicts
        console.warn('Could not validate session due to network error');
      }
    }
  }, [handleSessionConflict]);

  const logout = useCallback(() => {
    sessionStorage.setItem('natural_logout', 'true');
    removeAuthToken();
    localStorage.clear();
    sessionStorage.clear();
    setIsAuthenticated(false);
    window.location.href = "/login";
  }, []);

  const checkAuth = useCallback(() => {
    const token = getAuthToken();
    const isValid = !!token;
    setIsAuthenticated(isValid);
    setIsLoading(false);
    return isValid;
  }, []);

  useEffect(() => {
    checkAuth();

    const token = getAuthToken();
    if (!token) return;

    const watchInterval = setInterval(() => {
      checkForSessionConflict();
    }, 30000);

    const handleFocus = () => {
      checkForSessionConflict();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForSessionConflict();
      }
    };

    const handleStorageChange = (e) => {
      if (e.key === 'authToken') {
        if (!e.newValue && lastTokenRef.current) {
          const isNaturalLogout = sessionStorage.getItem('natural_logout') === 'true';
          if (!isNaturalLogout) {
            handleSessionConflict('cross_tab_conflict');
          }
        }
        lastTokenRef.current = e.newValue;
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(watchInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkAuth, checkForSessionConflict, handleSessionConflict]);

  return {
    isAuthenticated,
    isLoading,
    checkAuth,
    logout,
    handleSessionConflict,
  };
};

export default useAuth;